import {
  Skill,
  getSkillsSnapshot,
  openSkill,
  openSkillsPalette,
  rankSkills,
  subscribeSkills
} from '@/lib/skills';
import { cn } from '@/lib/utils';
import { LayoutGrid } from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';

import { ICommand, commandsState } from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import Icon from '@/components/Icon';
import {
  Command,
  CommandGroup,
  CommandItemAnimated,
  CommandListScrollable
} from '@/components/ui/command';

import { useCommandNavigation } from '@/hooks/useCommandNavigation';

import { skillIcon } from './SkillsPalette';

interface Props {
  id?: string;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  /** The next question offered by the backend. Rendered as the placeholder —
   *  never pre-typed — so a stray Enter cannot spend it. Tab accepts it. */
  suggestion?: string;
  selectedCommand?: ICommand;
  setSelectedCommand: (command: ICommand | undefined) => void;
  onChange: (value: string) => void;
  onPaste?: (event: any) => void;
  onEnter?: () => void;
}

export interface InputMethods {
  reset: () => void;
  setValueExtern: (value: string) => void;
}

const Input = forwardRef<InputMethods, Props>(
  (
    {
      placeholder,
      suggestion,
      id,
      className,
      autoFocus,
      selectedCommand,
      setSelectedCommand,
      onChange,
      onEnter,
      onPaste
    },
    ref
  ) => {
    const commands = useRecoilValue(commandsState);
    const [isComposing, setIsComposing] = useState(false);
    const [showCommands, setShowCommands] = useState(false);
    const [commandInput, setCommandInput] = useState('');
    const [value, setValue] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // "/" at the start of the box lists the skills: "All Skills" first (the
    // palette), then the best matches for what follows the slash. Only when
    // the session has no Chainlit commands of its own to show there.
    const skills = useSyncExternalStore(subscribeSkills, getSkillsSnapshot);
    const [skillIndex, setSkillIndex] = useState(0);
    const slashQuery =
      value.startsWith('/') && !value.includes('\n') ? value.slice(1) : null;
    const skillMatches: (Skill | null)[] =
      slashQuery !== null && skills.length && commands.length === 0
        ? [null, ...rankSkills(skills, slashQuery).slice(0, 6)]
        : [];
    const showSkills = skillMatches.length > 0;

    const pickSkill = (skill: Skill | null) => {
      setValue('');
      onChange('');
      setSkillIndex(0);
      if (skill) openSkill(skill.id);
      else openSkillsPalette();
    };

    const normalizedInput = commandInput.toLowerCase().slice(1);

    const filteredCommands = commands
      .filter((command) => command.id.toLowerCase().includes(normalizedInput))
      .sort((a, b) => {
        const indexA = a.id.toLowerCase().indexOf(normalizedInput);
        const indexB = b.id.toLowerCase().indexOf(normalizedInput);
        return indexA - indexB;
      });

    const {
      selectedIndex,
      handleMouseMove,
      handleMouseLeave,
      handleKeyDown: navigationKeyDown
    } = useCommandNavigation({
      items: filteredCommands,
      isOpen: showCommands,
      onSelect: (command) => {
        handleCommandSelect(command);
      },
      onClose: () => {
        setShowCommands(false);
        setCommandInput('');
      }
    });

    const reset = () => {
      setValue('');
      if (!selectedCommand?.persistent) {
        setSelectedCommand(undefined);
      }
      setCommandInput('');
      setShowCommands(false);
      onChange('');
    };

    useImperativeHandle(ref, () => ({
      reset,
      setValueExtern: (value: string) => {
        setValue(value);
        onChange(value);
      }
    }));

    useEffect(() => {
      if (textareaRef.current && autoFocus) {
        textareaRef.current.focus();
      }
    }, [autoFocus]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      onChange(newValue);
      setSkillIndex(0);

      // Command detection for dropdown
      const words = newValue.split(' ');
      if (words.length === 1 && words[0].startsWith('/')) {
        setShowCommands(true);
        setCommandInput(words[0]);
      } else {
        setShowCommands(false);
        setCommandInput('');
      }
    };

    const handleCommandSelect = (command: ICommand) => {
      setShowCommands(false);
      setSelectedCommand(command);

      // Remove the command text from the input
      const newValue = value.replace(commandInput, '').trimStart();
      setValue(newValue);
      onChange(newValue);

      setCommandInput('');

      // Focus back on textarea
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    };

    const acceptSuggestion = () => {
      if (!suggestion) return;
      setValue(suggestion);
      onChange(suggestion);
      textareaRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // The offered question sits in the placeholder until it is taken on
      // purpose. Tab takes it; Enter never does — an accidental send would
      // spend a question from the user's monthly cap on something they did
      // not ask.
      if (
        e.key === 'Tab' &&
        !e.shiftKey &&
        suggestion &&
        value === '' &&
        !showCommands
      ) {
        e.preventDefault();
        acceptSuggestion();
        return;
      }

      if (showSkills) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          const step = e.key === 'ArrowDown' ? 1 : -1;
          setSkillIndex((i) =>
            Math.max(0, Math.min(skillMatches.length - 1, i + step))
          );
          e.preventDefault();
          return;
        }
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
          pickSkill(
            skillMatches[Math.min(skillIndex, skillMatches.length - 1)]
          );
          e.preventDefault();
          return;
        }
        if (e.key === 'Escape') {
          setValue('');
          onChange('');
          e.preventDefault();
          return;
        }
      }

      // Handle command selection - check this FIRST before other key handling
      if (showCommands && filteredCommands.length > 0) {
        navigationKeyDown(e);
        // If the navigation handled the key, don't process further
        if (e.defaultPrevented) {
          return;
        }
      }

      // Handle regular enter only if command dropdown is actually visible
      if (
        e.key === 'Enter' &&
        !e.shiftKey &&
        onEnter &&
        !isComposing &&
        !(showCommands && filteredCommands.length > 0)
      ) {
        e.preventDefault();
        onEnter();
      }
    };

    return (
      <div className="relative w-full">
        <AutoResizeTextarea
          ref={textareaRef}
          id={id}
          autoFocus={autoFocus}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          placeholder={value === '' && suggestion ? suggestion : placeholder}
          // Arabic typed into an LTR textarea puts its punctuation on the wrong
          // side of the line while it is being written.
          dir="auto"
          className={cn(
            'w-full resize-none bg-transparent text-[14.5px] placeholder:text-muted-foreground focus:outline-none',
            className
          )}
          maxHeight={250}
        />

        {value === '' && suggestion ? (
          <button
            type="button"
            onClick={acceptSuggestion}
            title="Tab"
            aria-label="Use the suggested question"
            className="absolute top-0.5 right-0 rtl:right-auto rtl:left-0 rounded border border-border px-1.5 text-[11px] leading-5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            &#8677;
          </button>
        ) : null}

        {showSkills && (
          <div className="absolute z-50 left-0 bottom-full mb-3 w-[min(420px,90vw)] animate-slide-up">
            <div
              className="rounded-lg border shadow-md bg-background p-1.5"
              role="listbox"
              aria-label="Skills"
            >
              {skillMatches.map((sk, i) => {
                const Icon = sk ? skillIcon(sk) : LayoutGrid;
                return (
                  <div
                    key={sk ? sk.id : '__all'}
                    role="option"
                    aria-selected={i === skillIndex}
                    onMouseMove={() => setSkillIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickSkill(sk);
                    }}
                    className={cn(
                      'flex items-center gap-2.5 rounded-md px-2.5 py-2 cursor-pointer text-sm',
                      i === skillIndex && 'bg-muted',
                      !sk && 'border-b border-border rounded-b-none mb-1'
                    )}
                  >
                    <Icon className="!size-4 text-muted-foreground flex-none" />
                    <span className="flex-1 truncate font-medium">
                      {sk ? sk.label : 'All Skills'}
                    </span>
                    <span
                      className="text-xs text-muted-foreground truncate"
                      dir="auto"
                    >
                      {sk
                        ? sk.label_ar || sk.group
                        : `${skills.length} available`}
                    </span>
                  </div>
                );
              })}
              {skillMatches.length === 1 && slashQuery ? (
                <div className="px-2.5 py-2 text-xs text-muted-foreground">
                  No skill matches "{slashQuery}". Send it as a question
                  instead.
                </div>
              ) : null}
            </div>
          </div>
        )}

        {showCommands && filteredCommands.length > 0 && (
          <div
            className="absolute z-50 left-0 bottom-full mb-3 animate-slide-up"
            onMouseLeave={handleMouseLeave}
          >
            <Command className="rounded-lg border shadow-md bg-background">
              <CommandListScrollable maxItems={5} className="custom-scrollbar">
                <CommandGroup className="p-2">
                  {filteredCommands.map((command, index) => (
                    <CommandItemAnimated
                      key={command.id}
                      index={index}
                      isSelected={index === selectedIndex}
                      onMouseMove={() => handleMouseMove(index)}
                      onSelect={() => handleCommandSelect(command)}
                      className="command-item space-x-2"
                    >
                      <Icon
                        name={command.icon}
                        className={cn(
                          '!size-5 text-muted-foreground transition-transform duration-150',
                          index === selectedIndex && 'scale-110'
                        )}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{command.id}</div>
                        <div className="text-sm text-muted-foreground">
                          {command.description}
                        </div>
                      </div>
                    </CommandItemAnimated>
                  ))}
                </CommandGroup>
              </CommandListScrollable>
            </Command>
          </div>
        )}
      </div>
    );
  }
);

export default Input;
